import type { Locale } from "@engine/generate/conceptCopy.js";
import { getPwaUi } from "../i18n/pwaUi.js";

export type MountAboutViewOptions = {
  locale: Locale;
  onBackHome: () => void;
};

/**
 * Marketing / pitch page sourced from `writing/drafts/notanyai.md`.
 *
 * The pitch copy is English-only for now (it targets enterprise buyers), so the
 * body is not localized; only the back-navigation chrome follows `locale`.
 * Content is built with DOM nodes (not innerHTML) to stay consistent with the
 * privacy view and keep the markup escaped by construction.
 */
type Block =
  | { kind: "lede"; text: string }
  | { kind: "para"; text: string }
  | { kind: "bullets"; items: ReadonlyArray<string> }
  | { kind: "steps"; items: ReadonlyArray<string> }
  | { kind: "subhead"; text: string }
  | { kind: "example"; text: string }
  | { kind: "table"; table: ComparisonTable };

type Section = { id: string; heading: string; blocks: ReadonlyArray<Block> };

type ComparisonTable = {
  columns: ReadonlyArray<string>;
  rows: ReadonlyArray<{ label: string; cells: ReadonlyArray<string> }>;
  legend: string;
};

const COMPARISON: ComparisonTable = {
  columns: ["Capability", "NotebookLM", "Duolingo", "notanyai"],
  rows: [
    {
      label: "Adaptive + measurable competency model (per concept / per person)",
      cells: ["❌", "⚠️", "✅"],
    },
    { label: "Self-hostable (own cloud / VPC / on-prem)", cells: ["❌", "❌", "✅"] },
    { label: "Company-owned custom training content", cells: ["⚠️", "❌", "✅"] },
    { label: "Consumer engagement loop (streaks, gamified flows)", cells: ["❌", "✅", "✅"] },
    {
      label: "Regulated-enterprise readiness (compliance + deployment posture)",
      cells: ["❌", "❌", "✅"],
    },
    {
      label: "Primary use case today",
      cells: ["Document understanding", "Language learning", "Corporate training + assessment"],
    },
    {
      label: "Expansion roadmap",
      cells: ["❌", "❌", "Document-understanding + language-learning SubjectPacks [roadmap]"],
    },
  ],
  legend: "✅ = native fit · ⚠️ = partial / not core · ❌ = not designed for that use case.",
};

const SECTIONS: ReadonlyArray<Section> = [
  {
    id: "what",
    heading: "What notanyai is",
    blocks: [
      {
        kind: "lede",
        text: "AI literacy is the wedge. The company is a self-hostable, measurable training platform for regulated enterprise.",
      },
      {
        kind: "bullets",
        items: [
          "Adaptive learning engine + installable PWA.",
          "Shipping today: AI literacy course (15 linked concepts, EN/ES/EL), aligned to major 2026 frameworks.",
          "Core abstraction: SubjectPack (content layer), so the same engine can run onboarding, security, compliance, or product training.",
          "Can run in customer-controlled infrastructure (cloud account, VPC, on-prem).",
        ],
      },
    ],
  },
  {
    id: "how",
    heading: "How it works (first principles)",
    blocks: [
      {
        kind: "para",
        text: "Most “adaptive learning” products are spaced-repetition schedulers. notanyai is a stateful policy over a concept graph.",
      },
      {
        kind: "steps",
        items: [
          "Estimate learner state.",
          "Choose the highest-information next item.",
          "Update state.",
          "Repeat.",
        ],
      },
      { kind: "subhead", text: "Core entities" },
      {
        kind: "bullets",
        items: [
          "SubjectPack = what is taught (concept graph, copy, seed bank, locales, quest metadata).",
          "Format = how it is practiced (MCQ, Backpack, Spotlie, Redact).",
          "Trainer = assignment + constraints + review (roadmap authoring layer).",
          "Trainee = learner whose state evolves over time.",
        ],
      },
      { kind: "subhead", text: "Signal 1 — Elo difficulty" },
      {
        kind: "bullets",
        items: [
          "Learners and questions share a rating space.",
          "Correct above expectation moves rating up; misses move it down.",
          "Difficulty calibration stays online instead of manually labeled.",
        ],
      },
      {
        kind: "example",
        text: "A learner near 1180 solves a 1320 phishing item; next items move up in difficulty.",
      },
      { kind: "subhead", text: "Signal 2 — Curriculum graph" },
      {
        kind: "bullets",
        items: [
          "Concepts are a DAG with prerequisite edges.",
          "Selection uses graph structure (PageRank / HITS / betweenness / topological order).",
          "This preserves learning order and avoids random content jumps.",
        ],
      },
      {
        kind: "example",
        text: "Weak verification_workflow routes practice toward evidence and trust_reasoning before returning.",
      },
      { kind: "subhead", text: "Signal 3 — Propagating mastery" },
      {
        kind: "bullets",
        items: [
          "An answer updates local concept mastery and the nearby graph neighborhood.",
          "One interaction updates more than one tag.",
        ],
      },
      {
        kind: "example",
        text: "Repeated misses on deepfakes_scams reduce confidence on adjacent safety concepts; session composition shifts accordingly.",
      },
      { kind: "subhead", text: "End-to-end trace" },
      {
        kind: "steps",
        items: [
          "Learner starts with medium Elo and broad confidence.",
          "Misses two verification_workflow rounds in Spotlie.",
          "Mastery drops on that node and linked prerequisites.",
          "Next rounds in MCQ / Backpack bias toward that weak cluster.",
          "Dashboard shows concept-level recovery trajectory per learner.",
        ],
      },
    ],
  },
  {
    id: "why",
    heading: "Why buyers care",
    blocks: [
      {
        kind: "bullets",
        items: [
          "Sovereignty: can be deployed in customer infrastructure.",
          "Evidence: competency is tracked per person and per concept.",
          "Regulatory fit: supports AI-literacy and audit workflows in regulated orgs.",
          "Privacy controls: RLS isolation, anonymized retention strategy, deletion path.",
          "Portability: open stack, no proprietary runtime lock-in.",
        ],
      },
    ],
  },
  {
    id: "comparison",
    heading: "Quick comparison",
    blocks: [{ kind: "table", table: COMPARISON }],
  },
  {
    id: "enterprise",
    heading: "Enterprise and security readiness",
    blocks: [
      { kind: "subhead", text: "Solid today" },
      {
        kind: "bullets",
        items: [
          "Tenant isolation via Postgres RLS.",
          "Same artifact across managed cloud / customer VPC / on-prem.",
          "Open stack (static bundle + Postgres/Supabase primitives), no proprietary runtime.",
          "Passwordless auth, account deletion, anonymized long-term retention.",
        ],
      },
      { kind: "subhead", text: "Roadmap" },
      {
        kind: "bullets",
        items: [
          "SSO / SAML / OIDC + SCIM.",
          "RBAC + admin audit logs.",
          "SOC 2 Type II / ISO 27001 posture + external security reporting.",
          "Org assignment, dashboards, and evidence export.",
          "SLA-backed support and lifecycle docs.",
        ],
      },
    ],
  },
  {
    id: "formats",
    heading: "Four learning formats",
    blocks: [
      {
        kind: "bullets",
        items: [
          "Adaptive MCQ — direct concept checks with calibrated difficulty.",
          "Fill the Backpack — construct task-ready prompts/inputs.",
          "Spot the Lie — detect plausible but false claims.",
          "Redact Before Send — practice sensitive-data hygiene.",
        ],
      },
      { kind: "para", text: "All formats write into the same learner state." },
    ],
  },
  {
    id: "roadmap",
    heading: "Roadmap",
    blocks: [
      { kind: "subhead", text: "Shipping today" },
      {
        kind: "bullets",
        items: [
          "AI-literacy SubjectPack (EN/ES/EL), graph + seed/generative practice.",
          "Live “What is notanyai?” SubjectPack demo.",
          "Self-hostable deployment model.",
          "Per-learner progress tracking and deletion flow.",
        ],
      },
      { kind: "subhead", text: "Next [roadmap]" },
      {
        kind: "bullets",
        items: [
          "Self-serve SubjectPack authoring UI.",
          "Org assignment, dashboards, and evidence export.",
          "Enterprise auth/security upgrades (SSO/SCIM/RBAC/audit).",
          "Certification-style assessment modes.",
          "Language-learning SubjectPacks.",
        ],
      },
      { kind: "subhead", text: "Missing today: Trainer Authoring Layer [roadmap]" },
      {
        kind: "bullets",
        items: [
          "Upload source material and set learning goals.",
          "Generate SubjectPack drafts (concept map, seeds, distractors, explanations, coverage checks).",
          "Configure format mix (MCQ/Backpack/Spotlie/Redact).",
          "Configure difficulty policy (baseline, Elo bounds, thresholds).",
          "Review/publish with versioning and cohort assignment.",
        ],
      },
      { kind: "subhead", text: "Platform trajectory" },
      {
        kind: "bullets",
        items: [
          "One org, many SubjectPacks, one competency graph per learner.",
          "AI literacy is the wedge; enterprise training infrastructure is the long-term product.",
        ],
      },
    ],
  },
];

function renderBlock(block: Block): HTMLElement {
  switch (block.kind) {
    case "lede": {
      const p = document.createElement("p");
      p.className = "about-lede";
      p.textContent = block.text;
      return p;
    }
    case "para": {
      const p = document.createElement("p");
      p.className = "about-para";
      p.textContent = block.text;
      return p;
    }
    case "subhead": {
      const h = document.createElement("h3");
      h.className = "about-subhead";
      h.textContent = block.text;
      return h;
    }
    case "example": {
      const p = document.createElement("p");
      p.className = "about-example";
      const label = document.createElement("span");
      label.className = "about-example-label";
      label.textContent = "Example";
      p.appendChild(label);
      p.appendChild(document.createTextNode(" " + block.text));
      return p;
    }
    case "bullets":
    case "steps": {
      const list = document.createElement(block.kind === "steps" ? "ol" : "ul");
      list.className = block.kind === "steps" ? "about-steps" : "about-bullets";
      for (const item of block.items) {
        const li = document.createElement("li");
        li.textContent = item;
        list.appendChild(li);
      }
      return list;
    }
    case "table":
      return renderTable(block.table);
  }
}

function renderTable(table: ComparisonTable): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "about-table-wrap";

  const el = document.createElement("table");
  el.className = "about-table";

  const thead = document.createElement("thead");
  const headRow = document.createElement("tr");
  table.columns.forEach((col, i) => {
    const th = document.createElement("th");
    th.textContent = col;
    th.scope = "col";
    if (i > 0) th.className = "about-table-num";
    headRow.appendChild(th);
  });
  thead.appendChild(headRow);
  el.appendChild(thead);

  const tbody = document.createElement("tbody");
  for (const row of table.rows) {
    const tr = document.createElement("tr");
    const th = document.createElement("th");
    th.scope = "row";
    th.textContent = row.label;
    tr.appendChild(th);
    for (const cell of row.cells) {
      const td = document.createElement("td");
      td.className = "about-table-num";
      td.textContent = cell;
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  el.appendChild(tbody);
  wrap.appendChild(el);

  const legend = document.createElement("p");
  legend.className = "about-table-legend";
  legend.textContent = table.legend;
  wrap.appendChild(legend);

  return wrap;
}

export function mountAboutView(
  root: HTMLElement,
  { locale, onBackHome }: MountAboutViewOptions
): () => void {
  const ui = getPwaUi(locale);
  root.innerHTML = "";

  const article = document.createElement("article");
  article.className = "about";
  article.setAttribute("aria-labelledby", "about-title");

  const back = document.createElement("button");
  back.type = "button";
  back.className = "about-back";
  back.textContent = ui.privacyBackHome;
  back.addEventListener("click", () => onBackHome());
  article.appendChild(back);

  const title = document.createElement("h1");
  title.id = "about-title";
  title.className = "about-title";
  title.textContent = "notanyai";
  article.appendChild(title);

  for (const section of SECTIONS) {
    const sectionEl = document.createElement("section");
    sectionEl.className = "about-section";

    const h2 = document.createElement("h2");
    h2.className = "about-section-title";
    h2.textContent = section.heading;
    sectionEl.appendChild(h2);

    for (const block of section.blocks) {
      sectionEl.appendChild(renderBlock(block));
    }
    article.appendChild(sectionEl);
  }

  const cta = document.createElement("p");
  cta.className = "about-cta";
  const ctaLink = document.createElement("a");
  ctaLink.href = "https://notany.ai";
  ctaLink.textContent = "Try it → notany.ai";
  cta.appendChild(ctaLink);
  article.appendChild(cta);

  root.appendChild(article);

  return (): void => {
    if (article.parentElement === root) root.removeChild(article);
  };
}
